import { XMarkIcon } from '@heroicons/react/20/solid';
import { SmallSecondaryButton } from '@shared/components/buttons';
import { buttonInputClass, buttonInputInvalidClass } from '@shared/components/form-styles';
import { Field } from '@shared/components/forms';
import { useCallback } from 'react';
import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormRegister,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import { ColorInput } from './color-input';
import { COLORS } from './colors';
import type { FormValues } from './form-data';

type LabelsFormProps = {
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  register: UseFormRegister<FormValues>;
};

export function LabelsForm({ control, errors, register }: LabelsFormProps) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'labels',
  });

  // Watch the actual form values to get the real label data
  const watchedLabels = useWatch({
    control,
    name: 'labels',
  });

  const addLabel = useCallback(() => {
    // Use the watched labels to get the actual numerical IDs
    const currentLabels = watchedLabels || [];
    const nextId =
      currentLabels.length > 0 ? Math.max(...currentLabels.map((label) => label.id)) + 1 : 1;

    // use next available color, or fallback to colors by index order if all in use
    const nextColor =
      COLORS.find((color) => !currentLabels.some((label) => label.color === color)) ||
      COLORS[nextId % COLORS.length];

    append({
      id: nextId,
      label: '',
      color: nextColor,
    });
  }, [append, watchedLabels]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 items-start max-h-96 overflow-y-auto">
        {fields.map((field, index) => (
          <Field
            label=""
            name={`labels.${index}.label`}
            errorMessage={errors.labels?.[index]?.label?.message}
            key={field.id}
          >
            <div className="flex flex-row gap-2 items-center">
              <Controller
                name={`labels.${index}.color`}
                control={control}
                render={({ field }) => (
                  <ColorInput value={field.value ?? ''} onChange={field.onChange} />
                )}
              />
              <div className="flex rounded-md shadow-xs">
                <div className="relative flex grow items-stretch focus-within:z-10">
                  <input
                    key={field.id}
                    {...register(`labels.${index}.label`)}
                    className={
                      errors.labels?.[index]?.label?.message
                        ? buttonInputInvalidClass
                        : buttonInputClass
                    }
                    placeholder="Label Name"
                    type="text"
                  />
                </div>
                <button
                  type="button"
                  className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-none focus-visible:ring-primary-600"
                  onClick={() => remove(index)}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </Field>
        ))}
      </div>
      <SmallSecondaryButton type="button" onClick={addLabel} disabled={fields.length >= 14}>
        Add Label
      </SmallSecondaryButton>
    </div>
  );
}
